import { StepStatusEnum, Wizard } from '@driftt/dds'
import { WizardRootProps } from '@driftt/dds/dist/packages/core/src/lib/components/Wizard/Wizard.types'
import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { withRouter } from 'react-router-dom'

import {
  CreatePlaybookWithFLSettingsMutation,
  FastlaneSettingsType,
  FetchFLSettingsQuery,
} from 'api/fastlane'
import { extractURLDomainAndPath } from 'components/Fastlane/shared/utils'
import useDocumentTitle from 'hooks/usePageTitle'
import useWorkspacesGeoTargeting from 'hooks/useWorkspacesGeoTargeting'
import { getPlaybookCampaignId } from 'utils/playbooks'

import {
  FLWizardContextType,
  FLWizardStoreProvider,
  useFLWizardStore,
  validateFLWizardContext,
} from './FLCreationWizard.context'
import { useTrackWizardCancelEvent } from './FLCreationWizard.hooks'
import { FLCreationWizardStepFormConfig } from './FLCreationWizardStepFormConfig'
import { FLCreationWizardStepName } from './FLCreationWizardStepName'
import { FLCreationWizardStepURL } from './FLCreationWizardStepURL'
import { FLCreationWizardTest } from './FLCreationWizardTest'

const isPlaybookManual = ({
  isDeferredSubmission,
  isManualFL,
  form,
}: FLWizardContextType) => {
  if (isManualFL || isDeferredSubmission || !form?.quickSetupCompatible) {
    return true
  }
  return false
}

type StepStatusLiteral = typeof StepStatusEnum[keyof typeof StepStatusEnum]

export type FLWizardStep = {
  /** Tracking ID used for segment tracking, should not change regardless of step reordering */
  trackingId: string
} & WizardRootProps['steps'][0]

const step1Status = (wizardStore: FLWizardContextType): StepStatusLiteral => {
  if (wizardStore.playbookName) {
    return StepStatusEnum.COMPLETE
  }
  return StepStatusEnum.INCOMPLETE
}

const step2Status = (wizardStore: FLWizardContextType): StepStatusLiteral => {
  if (!wizardStore.playbookName) {
    return StepStatusEnum.LOCKED
  }
  if (wizardStore.form) {
    return StepStatusEnum.COMPLETE
  }
  if (wizardStore.isManualFL) {
    return StepStatusEnum.COMPLETE
  }
  return StepStatusEnum.INCOMPLETE
}

const step3Status = (wizardStore: FLWizardContextType): StepStatusLiteral => {
  if (!wizardStore.formUrl) {
    return StepStatusEnum.LOCKED
  }
  if (wizardStore.testSuccess) {
    return StepStatusEnum.COMPLETE
  }
  if (wizardStore.isManualFL) {
    return StepStatusEnum.COMPLETE
  }
  return StepStatusEnum.INCOMPLETE
}

const step4Status = (wizardStore: FLWizardContextType): StepStatusLiteral => {
  if (!wizardStore.testSuccess) {
    return StepStatusEnum.LOCKED
  }
  if (wizardStore.hasRedirect != null) {
    if (wizardStore.hasRedirect && wizardStore.redirectUrl) {
      return StepStatusEnum.COMPLETE
    }
    if (!wizardStore.hasRedirect) {
      return StepStatusEnum.COMPLETE
    }
  }
  if (wizardStore.isDeferredSubmission) {
    return StepStatusEnum.COMPLETE
  }
  return StepStatusEnum.INCOMPLETE
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CreationWizard = ({ history }: any) => {
  const queryClient = useQueryClient()
  useDocumentTitle('Drift | Fastlane')

  const [activeStep, setActiveStep] = useState(0)
  const {
    mutate: createPlaybookWithFLSettings,
    isLoading: createPlaybookLoading,
  } = useMutation({
    mutationKey: CreatePlaybookWithFLSettingsMutation.mutationKey,
    mutationFn: CreatePlaybookWithFLSettingsMutation.mutationFn,
    onSuccess: ({ playbookData, fastlaneData }) => {
      const campaignId = getPlaybookCampaignId(playbookData)
      if (campaignId) {
        queryClient.setQueryData(
          FetchFLSettingsQuery.queryKey(campaignId),
          fastlaneData
        )
      }
      history.replace(`/editor/${playbookData.id}`)
    },
  })

  const { orgHasWorkspaces } = useWorkspacesGeoTargeting()

  const { store: wizardStore } = useFLWizardStore()

  const steps: FLWizardStep[] = [
    {
      label: 'Step 1: Name',
      status: step1Status(wizardStore),
      trackingId: 'fl-wizard-name',
    },
    {
      label: 'Step 2: URL',
      status: step2Status(wizardStore),
      trackingId: 'fl-wizard-form-url',
    },
    ...(wizardStore.form?.quickSetupCompatible
      ? [
          {
            label: 'Step 3: Test',
            status: step3Status(wizardStore),
            trackingId: 'fl-wizard-test',
          },
          ...(!wizardStore.isManualFL
            ? [
                {
                  label: 'Step 4: Form configuration',
                  status: step4Status(wizardStore),
                  trackingId: 'fl-wizard-form-config',
                },
              ]
            : []),
        ]
      : []),
  ]

  useTrackWizardCancelEvent({
    history,
    activeStep,
    steps,
  })

  const canSubmit =
    steps.every((step) => step.status === StepStatusEnum.COMPLETE) &&
    !createPlaybookLoading

  const handleSubmit = () => {
    if (validateFLWizardContext(wizardStore, { orgHasWorkspaces })) {
      createPlaybookWithFLSettings({
        playbookSettings: {
          name: wizardStore.playbookName,
          workspaceId: orgHasWorkspaces ? wizardStore.workspaceId : '',
        },
        fastlaneSettings: {
          formType: wizardStore.form.type,
          formHtmlId: wizardStore.form.id ?? '',
          detectedFormUrl: extractURLDomainAndPath(wizardStore.formUrl),
          deferredFormSubmission: wizardStore.isDeferredSubmission,
          fastlaneSettingsType: isPlaybookManual(wizardStore)
            ? FastlaneSettingsType.MANUAL
            : FastlaneSettingsType.QUICK_SETUP,
          redirectUrl: wizardStore.hasRedirect ? wizardStore.redirectUrl : '',
        },
        formFields: wizardStore.form.fields,
      })
    }
  }

  return (
    <Wizard.Root
      activeStep={activeStep}
      open={true}
      onClose={() => history.goBack()}
      onSubmit={handleSubmit}
      onStepChange={setActiveStep}
      steps={steps}
      canSubmit={canSubmit}
      submitButtonText="Create Fastlane playbook"
    >
      <Wizard.Dialog title="Create Fastlane playbook">
        <FLCreationWizardStepName />
        <FLCreationWizardStepURL />
        <FLCreationWizardTest />
        <FLCreationWizardStepFormConfig />
      </Wizard.Dialog>
    </Wizard.Root>
  )
}

const CreationWizardWithRouter = withRouter(CreationWizard)

export const FLCreationWizard = () => (
  <FLWizardStoreProvider>
    <CreationWizardWithRouter />
  </FLWizardStoreProvider>
)
